import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ListTodo, CheckCircle2, Brain, ArrowLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { EmployeePicker } from "../../components/os/EmployeePicker";
import { api } from "../../lib/api";

const CAT_LABEL = { liderazgo: "Liderazgo", estructura: "Estructura", rotacion: "Rotación", ausentismo: "Ausentismo" };
const CAT_CLS = { liderazgo: "bg-violet/10 text-violet", estructura: "bg-sky-500/15 text-sky-600", rotacion: "bg-amber-500/15 text-amber-600", ausentismo: "bg-emerald-500/15 text-emerald-600" };
const today = () => new Date().toISOString().slice(0, 10);

export default function PeopleActions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/people/action-items").then((r) => setItems(r.data || [])).catch(() => setError(true)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (a, body, key) => {
    setBusy(key);
    try {
      const { data } = await api.patch(`/people/action-items/${a.id}`, body);
      setItems((prev) => prev.map((x) => (x.id === a.id ? { ...x, ...data } : x)));
      if (body.status === "done") toast.success("Acción completada");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(null); }
  };

  const open = useMemo(() => items.filter((a) => a.status === "open"), [items]);
  const done = useMemo(() => items.filter((a) => a.status === "done"), [items]);
  const overdue = useMemo(() => open.filter((a) => a.due_date && a.due_date < today()).length, [open]);

  const kpis = [
    { l: "Acciones totales", v: items.length },
    { l: "En curso", v: open.length },
    { l: "Completadas", v: done.length },
    { l: "Vencidas", v: overdue },
  ];

  return (
    <OSLayout title="Panel de Acciones RH">
      <div className="max-w-5xl space-y-6" data-testid="people-actions">
        <div>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><ListTodo className="w-3.5 h-3.5" /> OMNIA People Suite · Centro de Acción</div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">Panel de Acciones RH</h2>
            <Link to="/os/people-intelligence" className="btn btn-ghost gap-1.5 text-sm" data-testid="pa-back-intel"><ArrowLeft className="w-4 h-4" /> People Intelligence</Link>
          </div>
          <p className="mt-2 text-navy/50 max-w-2xl">Todas las acciones creadas desde las recomendaciones de la IA, con responsable y fecha límite.</p>
        </div>

        {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> :
          error ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">No disponible para tu rol.</div> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pa-kpis">
              {kpis.map((k) => (
                <div key={k.l} className="bg-white border border-navy/10 rounded-2xl p-4">
                  <div className="text-2xl font-medium text-navy">{k.v}</div>
                  <div className="text-xs text-navy/45">{k.l}</div>
                </div>
              ))}
            </div>

            <div>
              <h3 className="font-editorial text-2xl mb-3 flex items-center gap-2"><ListTodo className="w-4 h-4 text-violet" /> En curso ({open.length})</h3>
              {open.length === 0 ? (
                <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40" data-testid="pa-open-empty">
                  Sin acciones en curso. Acepta recomendaciones en <Link to="/os/people-intelligence" className="text-violet underline">People Intelligence</Link>.
                </div>
              ) : (
                <div className="space-y-3">
                  {open.map((a) => {
                    const isOverdue = a.due_date && a.due_date < today();
                    return (
                      <div key={a.id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`pa-item-${a.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-navy">{a.title}</span>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full ${CAT_CLS[a.category] || "bg-navy/10 text-navy/50"}`}>{CAT_LABEL[a.category] || a.category}</span>
                              {isOverdue && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Vencida</span>}
                            </div>
                            <p className="text-sm text-navy/55 mt-1">{a.detail}</p>
                          </div>
                          <button onClick={() => patch(a, { status: "done" }, "done" + a.id)} disabled={busy === "done" + a.id} className="btn btn-ghost text-xs gap-1.5 text-emerald-600 shrink-0" data-testid={`pa-done-${a.id}`}>
                            {busy === "done" + a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Marcar hecho
                          </button>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3 mt-3">
                          <div>
                            <label className="text-[11px] text-navy/50 mb-1 block">Responsable</label>
                            <EmployeePicker testid={`pa-assignee-${a.id}`} value={a.assignee_id} placeholder="Asignar responsable"
                              onChange={(emp) => patch(a, { assignee_id: emp?.id || "", assignee_name: emp?.name || "" }, "assignee" + a.id)} />
                          </div>
                          <div>
                            <label className="text-[11px] text-navy/50 mb-1 block">Fecha límite</label>
                            <input type="date" className="field" value={a.due_date || ""} data-testid={`pa-due-${a.id}`}
                              onChange={(e) => patch(a, { due_date: e.target.value }, "due" + a.id)} />
                          </div>
                        </div>
                        <div className="text-[11px] text-navy/35 mt-2">Creada por {a.created_by_name} · {(a.created_at || "").slice(0, 10)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {done.length > 0 && (
              <div>
                <h3 className="font-editorial text-xl mb-3 flex items-center gap-2 text-navy/60"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Completadas ({done.length})</h3>
                <div className="space-y-2">
                  {done.map((a) => (
                    <div key={a.id} className="bg-white/60 border border-navy/10 rounded-xl p-3 flex items-center gap-3" data-testid={`pa-done-item-${a.id}`}>
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      <div className="flex-1 min-w-0"><div className="text-sm text-navy/60 line-through truncate">{a.title}</div><div className="text-[11px] text-navy/35">{a.assignee_name ? `Responsable: ${a.assignee_name}` : "Sin responsable"}</div></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </OSLayout>
  );
}
